import React, { Suspense, lazy } from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Provider } from "react-redux";
import jwt_decode from "jwt-decode";

import Header from "./components/layout/Header";
import store from "./store";
import setAuthToken from "./utils/setAuthToken";
import { setCurrentClient, logoutClient } from "./actions/authActions";
import PageTracker from "./utils/PageTracker";

const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const HomePage = lazy(() => import("./containers/layout/HomePage"));
const BlogPage = lazy(() => import("./containers/layout/BlogPage"));
const BlogDetailPage = lazy(() => import("./containers/layout/BlogDetailPage"));
const LoginPage = lazy(() => import("./containers/layout/LoginPage"));
const DashboardPage = lazy(() => import("./containers/admin/DashboardPage"));
const BlogAdminPage = lazy(() => import("./containers/admin/BlogAdminPage"));
const BlogDetailAdminPage = lazy(() => import("./containers/admin/BlogDetailAdminPage"));
const CheatsheetDetailAdminPage = lazy(() => import("./containers/admin/CheatsheetDetailAdminPage"));
const UpdateArticlePage = lazy(() => import("./containers/admin/UpdateArticlePage"));
const UpdateCheatsheetPage = lazy(() => import("./containers/admin/UpdateCheatsheetPage"));
const CheatsheetPage = lazy(() => import("./containers/layout/CheatsheetPage"));
const AddArticlePage = lazy(() => import("./containers/admin/AddArticlePage"));
const SettingsPage = lazy(() => import("./containers/admin/SettingsPage"));
const ProjectsAdminPage = lazy(() => import("./containers/admin/ProjectsAdminPage"));
const SkillsAdminPage = lazy(() => import("./containers/admin/SkillsAdminPage"));
const CommentsAdminPage = lazy(() => import("./containers/admin/CommentsAdminPage"));
const AnalyticsPage = lazy(() => import("./containers/admin/AnalyticsPage"));
const InsightsPage = lazy(() => import("./containers/admin/InsightsPage"));
const TimeTrackingPage = lazy(() => import("./containers/admin/TimeTrackingPage"));
const SubscribersAdminPage = lazy(() => import("./containers/admin/SubscribersAdminPage"));
const ContentPlannerPage = lazy(() => import("./containers/admin/ContentPlannerPage"));
const SeriesAdminPage = lazy(() => import("./containers/admin/SeriesAdminPage"));
const CategoriesAdminPage = lazy(() => import("./containers/admin/CategoriesAdminPage"));
const TagsAdminPage = lazy(() => import("./containers/admin/TagsAdminPage"));
const ContentPlanningPage = lazy(() => import("./containers/layout/ContentPlanningPage"));
const CheatAdminPage = lazy(() => import("./containers/admin/CheatAdminPage"));
const AddCheatPage = lazy(() => import("./containers/admin/AddCheatPage"));
const CheatsheetDetailPage = lazy(() => import("./containers/layout/CheatsheetDetailPage"));
const NewsletterListPage = lazy(() => import("./containers/admin/NewsletterListPage"));
const NewsletterEditorPage = lazy(() => import("./containers/admin/NewsletterEditorPage"));
const NewsletterPreviewPage = lazy(() => import("./containers/admin/NewsletterPreviewPage"));


if (localStorage.jwtToken) {
  const token = localStorage.jwtToken;
  setAuthToken(token);
  const decoded = jwt_decode(token);
  store.dispatch(setCurrentClient(decoded));

  const currentTime = Date.now() / 1000;
  if (decoded.exp < currentTime) {
    store.dispatch(logoutClient());
    window.location.href = "./login";
  }
}

export const handleClick = () => {
  store.dispatch(logoutClient());
};

const RouteFallback = () => (
  <div className="preloader">
    <div className="preloader-animation">
      <div className="preloader-spinner"></div>
    </div>
  </div>
);

const App = () => {
  return (
    <HelmetProvider>
      <Provider store={store}>
        <BrowserRouter>
          <div className="ta-main-wrapper">
            <PageTracker />
            <Header />

            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/cheat-sheet" element={<CheatsheetPage />} />
                <Route path="/blog/:linkFormat" element={<BlogDetailPage />} />
                <Route path="/cheat-sheet/:linkFormat" element={<CheatsheetDetailPage />} />
                <Route path="/content-planning" element={<ContentPlanningPage />} />
                <Route path="/login" element={<LoginPage />} />

                <Route element={<AdminLayout />}>
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/dashboard/blog" element={<BlogAdminPage />} />
                  <Route path="/dashboard/blog/new" element={<AddArticlePage />} />
                  <Route path="/dashboard/blog/:linkFormat" element={<BlogDetailAdminPage />} />
                  <Route path="/dashboard/cheat-sheet" element={<CheatAdminPage />} />
                  <Route path="/dashboard/cheat-sheet/new" element={<AddCheatPage />} />
                  <Route path="/dashboard/cheatsheet/:linkFormat" element={<CheatsheetDetailAdminPage />} />
                  <Route path="/dashboard/projects" element={<ProjectsAdminPage />} />
                  <Route path="/dashboard/skills" element={<SkillsAdminPage />} />
                  <Route path="/dashboard/analytics" element={<AnalyticsPage />} />
                  <Route path="/dashboard/insights" element={<InsightsPage />} />
                  <Route path="/dashboard/time-tracking" element={<TimeTrackingPage />} />
                  <Route path="/dashboard/comments" element={<CommentsAdminPage />} />
                  <Route path="/dashboard/subscribers" element={<SubscribersAdminPage />} />
                  <Route path="/dashboard/content-planner" element={<ContentPlannerPage />} />
                  <Route path="/dashboard/series" element={<SeriesAdminPage />} />
                  <Route path="/dashboard/categories" element={<CategoriesAdminPage />} />
                  <Route path="/dashboard/tags" element={<TagsAdminPage />} />
                  <Route path="/dashboard/settings" element={<SettingsPage />} />
                  <Route path="/dashboard/newsletter" element={<NewsletterListPage />} />
                  <Route path="/dashboard/newsletter/:id" element={<NewsletterEditorPage />} />
                  <Route path="/dashboard/newsletter/:id/preview" element={<NewsletterPreviewPage />} />
                  <Route path="/admin/updateArticle/:id" element={<UpdateArticlePage />} />
                  <Route path="/admin/updateCheatsheet/:id" element={<UpdateCheatsheetPage />} />
                </Route>

                <Route path="*" element={<Navigate to={"/"} />} />
              </Routes>
            </Suspense>
          </div>
        </BrowserRouter>
      </Provider>
    </HelmetProvider>
  );
};

export default App;
